import "zod/compile";
import { z } from "zod";

/** A column of one Source: where a Sampling key or a declared stratum is read. */
export const workbookSourceColumnSchema = z.object({
  sourceId: z.uuid(),
  column: z.string().min(1).max(200),
});
export type WorkbookSourceColumn = z.infer<typeof workbookSourceColumnSchema>;

/** The most key values a Sampled run takes. */
export const WORKBOOK_MAX_SAMPLE_KEYS = 10_000;

/**
 * Sampling: how a Sampled run picks its subset. It samples by key, not by
 * row: `size` values of the Sampling key, and every row carrying one of them
 * in every Source that has the key (`keyColumns`, the key's column in each),
 * so lookups between those Sources still match. Other Sources are narrowed
 * through the Workbook's own lookups where they can be, else kept whole.
 * Strata (`strata`, plus automatic ones when `automaticStrata`) each get at
 * least `minPerStratum` keys, so edge cases always appear. The same seed,
 * Sampling and data give the same sample. Presentational: never
 * Compute-relevant, so editing it leaves every Result Current.
 */
export const workbookSamplingSchema = z.object({
  keyColumns: z.array(workbookSourceColumnSchema).min(1).max(20),
  size: z.number().int().min(1).max(WORKBOOK_MAX_SAMPLE_KEYS).default(200),
  seed: z.number().int().min(0).max(2_147_483_647).default(1),
  /** Columns whose values each make a stratum (Hub vs Direct, a pop code). */
  strata: z.array(workbookSourceColumnSchema).max(10).default([]),
  minPerStratum: z.number().int().min(0).max(100).default(3),
  /** Blanks, text in a number column, the top 1% of a number column, and lookup misses, as strata. */
  automaticStrata: z.boolean().default(true),
});
export type WorkbookSampling = z.infer<typeof workbookSamplingSchema>;
export type WorkbookSamplingInput = z.input<typeof workbookSamplingSchema>;

/**
 * How a Sampled run reads one Source: by the Sampling key (`key`), by the
 * values the sampled rows look up in it (`derived`), or all of it (`whole`).
 */
export const WORKBOOK_SAMPLE_SOURCE_METHODS = [
  "key",
  "derived",
  "whole",
] as const;
export const workbookSampleSourceMethodSchema = z.enum(
  WORKBOOK_SAMPLE_SOURCE_METHODS,
);
export type WorkbookSampleSourceMethod = z.infer<
  typeof workbookSampleSourceMethodSchema
>;

export const WORKBOOK_SAMPLE_STRATUM_KINDS = [
  "declared",
  "blank",
  "text-in-number",
  "top",
  "lookup-miss",
  "blank-key",
] as const;
export const workbookSampleStratumKindSchema = z.enum(
  WORKBOOK_SAMPLE_STRATUM_KINDS,
);
export type WorkbookSampleStratumKind = z.infer<
  typeof workbookSampleStratumKindSchema
>;

const countPair = z.object({
  sample: z.number().int().nonnegative(),
  full: z.number().int().nonnegative(),
});
/**
 * A rate (0 to 1) over the full data, the whole sample, and the sample's
 * random part: the keys taken to fill it after every stratum had its
 * minimum. Only the random part is meant to look like the full data; the
 * strata oversample edge cases on purpose. Null when nothing was counted.
 */
const ratePair = z.object({
  full: z.number().min(0).max(1).nullable(),
  sample: z.number().min(0).max(1).nullable(),
  random: z.number().min(0).max(1).nullable(),
});
export type WorkbookSampleRate = z.infer<typeof ratePair>;

/** The most strata, columns and lookups a Representativeness report lists. */
export const WORKBOOK_SAMPLE_REPORT_ITEMS = 200;

/**
 * A Representativeness report: how a Sampled run's subset compares with the
 * full data it was taken from, per Source, stratum, column and lookup, with
 * its warnings. `distance` is the largest gap between the random part's
 * rates and the full data's (0 is none, 1 is total).
 */
export const workbookSamplingReportSchema = z.object({
  keys: countPair,
  sources: z
    .array(
      z.object({
        sourceId: z.uuid(),
        method: workbookSampleSourceMethodSchema,
        /** The key's column (`key`), or the column narrowed by lookups (`derived`). */
        column: z.string().nullable(),
        rows: countPair,
      }),
    )
    .max(20),
  strata: z
    .array(
      z.object({
        kind: workbookSampleStratumKindSchema,
        sourceId: z.uuid(),
        column: z.string(),
        /** A declared stratum's value; null for the automatic kinds. */
        value: z.string().nullable(),
        keys: countPair,
      }),
    )
    .max(WORKBOOK_SAMPLE_REPORT_ITEMS),
  columns: z
    .array(
      z.object({
        sourceId: z.uuid(),
        column: z.string(),
        blankRate: ratePair,
      }),
    )
    .max(WORKBOOK_SAMPLE_REPORT_ITEMS),
  lookups: z
    .array(
      z.object({
        sheetId: z.uuid(),
        from: z.object({ sourceId: z.uuid(), column: z.string() }),
        to: z.object({ sourceId: z.uuid(), column: z.string() }),
        matchRate: ratePair,
      }),
    )
    .max(WORKBOOK_SAMPLE_REPORT_ITEMS),
  distance: z.number().min(0).max(1),
  warnings: z.array(z.string()).max(50),
});
export type WorkbookSamplingReport = z.infer<
  typeof workbookSamplingReportSchema
>;

/** What a Sampled run recorded about its sample. */
export const workbookRunSamplingSchema = z.object({
  /** The Sampling the Run took its sample by. */
  sampling: workbookSamplingSchema,
  /** A hash of the Sampling, the sampling plan and each Source's Fingerprint: equal keys, the same sample. */
  key: z.string(),
  /** The Sampled run whose sample this one reused instead of sampling again; null when it sampled. */
  reusedRunId: z.uuid().nullable(),
  /** Absent until the Run has sampled. */
  report: workbookSamplingReportSchema.nullable(),
});
export type WorkbookRunSampling = z.infer<typeof workbookRunSamplingSchema>;

/** What a Run is compared with: the previous comparable Run, or (a full Run) the Sampled run before it. */
export const workbookRunComparisonKindSchema = z.enum(["previous", "sample"]);
export type WorkbookRunComparisonKind = z.infer<
  typeof workbookRunComparisonKindSchema
>;

const figurePair = z.object({
  baseline: z.number().nullable(),
  current: z.number().nullable(),
});

/**
 * A completed Run compared with an earlier one (its baseline), per
 * Worksheet both computed: row counts, each lookup's miss rate, few-valued
 * Text columns' value shares and Number columns' totals, the baseline's
 * counts and totals scaled by `scale` (the share of keys a Sampled baseline
 * took, inverted; 1 otherwise), with a warning for each large shift.
 * Warnings never fail a Run or block a download.
 */
export const workbookRunComparisonSchema = z.object({
  kind: workbookRunComparisonKindSchema,
  baselineRunId: z.uuid(),
  scale: z.number().positive(),
  sheets: z.array(
    z.object({
      sheetId: z.uuid(),
      /** The Worksheet's settings changed between the two Runs (its Fingerprint differs). */
      changed: z.boolean(),
      rows: figurePair,
      /** Each lookup's share of rows that found no match (0 to 1), by the column holding its result. */
      lookups: z.array(z.object({ column: z.string(), missRate: figurePair })),
      /** Total variation distance between the two Runs' value shares (0 to 1). */
      categories: z.array(
        z.object({ column: z.string(), distance: z.number().min(0).max(1) }),
      ),
      totals: z.array(
        z.object({ column: z.string() }).extend(figurePair.shape),
      ),
    }),
  ),
  /** Each warning in words, for the agent and logs. */
  warnings: z.array(z.string()).max(50),
  /** The same warnings as data, for the UI to put in the user's language. */
  alerts: z
    .array(
      z.object({
        kind: z.enum(["rows", "miss-rate", "categories", "total"]),
        sheetId: z.uuid(),
        /** The column it is about; null for a row count. */
        column: z.string().nullable(),
        /** The baseline's figure (scaled) and this Run's; for categories, the share of rows that moved. */
        baseline: z.number().nullable(),
        current: z.number().nullable(),
      }),
    )
    .max(50)
    .default([]),
});
export type WorkbookRunComparison = z.infer<typeof workbookRunComparisonSchema>;
export type WorkbookRunComparisonAlert =
  WorkbookRunComparison["alerts"][number];

/** A Worksheet's Number columns' totals over its whole Result, as a Run recorded them. */
export const workbookResultTotalsSchema = z
  .array(z.object({ column: z.string(), total: z.number().nullable() }))
  .max(100);
